import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Copy, Check, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";
import type { DigitalProposal } from "@/types/digitalProposal";

export default function DigitalProposalReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState<DigitalProposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data, error } = await supabase.from("digital_proposals").select("*").eq("id", id).single();
      if (!error) setProposal(data as unknown as DigitalProposal);
      setLoading(false);
    })();
  }, [id]);

  const shareUrl = proposal ? `${window.location.origin}/digital-proposal/${proposal.share_id}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Share link copied!");
    setTimeout(() => setCopied(false), 1500);
  };

  const markSent = async () => {
    if (!proposal) return;
    await supabase
      .from("digital_proposals")
      .update({ status: "sent", sent_at: new Date().toISOString() })
      .eq("id", proposal.id);
    toast.success("Marked as sent");
    setProposal({ ...proposal, status: "sent" });
  };

  if (loading) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  if (!proposal) return <div className="p-12 text-center">Not found</div>;

  return (
    <div className="container mx-auto px-6 py-6 max-w-5xl">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4 gap-2">
        <ArrowLeft className="w-4 h-4" /> Back
      </Button>

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold">{proposal.title}</h1>
            <Badge>{proposal.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">For {proposal.customer_name}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={copyLink} className="gap-2">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            Copy Share Link
          </Button>
          <Button variant="outline" onClick={() => window.open(shareUrl, "_blank")} className="gap-2">
            <ExternalLink className="w-4 h-4" /> Open Customer View
          </Button>
          {proposal.status === "draft" && (
            <Button onClick={markSent} className="gap-2">
              <Send className="w-4 h-4" /> Mark as Sent
            </Button>
          )}
        </div>
      </div>

      <Card className="p-6 mb-6">
        <h2 className="font-semibold mb-3">Tiers</h2>
        <div className="grid md:grid-cols-3 gap-3">
          {proposal.tiers.map((t) => (
            <div key={t.key} className="border rounded-lg p-4">
              <div className="text-xs uppercase text-muted-foreground">{t.key}</div>
              <div className="font-semibold">{t.name}</div>
              {t.manufacturer && (
                <div className="text-xs text-muted-foreground">{t.manufacturer} · {t.product_line}</div>
              )}
              <div className="text-2xl font-bold mt-2">${t.price.toLocaleString()}</div>
              <div className="flex gap-1 mt-2">
                {(t.colors || []).slice(0, 6).map((c) => (
                  <div key={c.hex} className="w-4 h-4 rounded-full border" style={{ backgroundColor: c.hex }} title={c.name} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {proposal.status === "signed" && (
        <Card className="p-6 bg-success/5 border-success/30">
          <h2 className="font-semibold mb-2">✓ Signed</h2>
          <p className="text-sm">
            By <strong>{proposal.signed_by_name}</strong>
            {proposal.signed_at ? ` on ${new Date(proposal.signed_at).toLocaleString()}` : ""}
          </p>
          {proposal.selected_tier && (
            <p className="text-sm mt-1">
              Selected: <strong>{proposal.tiers.find((t) => t.key === proposal.selected_tier)?.name}</strong>
              {proposal.selected_color ? ` in ${proposal.selected_color.name}` : ""}
            </p>
          )}
          {proposal.signature_data && (
            <img src={proposal.signature_data} alt="signature" className="h-16 mt-3 bg-white border rounded" />
          )}
        </Card>
      )}
    </div>
  );
}
